import { toast } from '@postscript/components';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useHasBillingAccess } from 'components/billing/common/hooks/useHasBillingAccess';
import { formatErrorMessage } from 'utils/errors';
import { api } from 'controllers/network/apiClient';

export interface SpendNotificationSettings {
  enabled: boolean;
  thresholdAmount: number; // cents
}

export const SPEND_NOTIFICATION_SETTINGS = 'spend_notification_settings';

const staleTime = 1000 * 60;

function getSpendNotificationSettings(): Promise<SpendNotificationSettings> {
  return api.get(
    '/v2/billing/transactional_messaging/usage_spend_alert_settings',
  );
}

export const useGetSpendNotificationSettings = (
  options?: UseQueryOptions<
    SpendNotificationSettings,
    any,
    SpendNotificationSettings,
    [typeof SPEND_NOTIFICATION_SETTINGS]
  >,
): UseQueryResult<SpendNotificationSettings> => {
  const hasBillingAccess = useHasBillingAccess();
  return useQuery({
    queryKey: [SPEND_NOTIFICATION_SETTINGS],
    queryFn: getSpendNotificationSettings,
    ...options,
    staleTime,
    enabled: (options?.enabled ?? true) && hasBillingAccess,
  });
};

function setSpendNotificationSettings(
  settings: SpendNotificationSettings,
): Promise<SpendNotificationSettings> {
  return api.post(
    '/v2/billing/transactional_messaging/usage_spend_alert_settings',
    settings,
  );
}

export const useSetSpendNotificationSettings = (): UseMutationResult<
  SpendNotificationSettings,
  any,
  SpendNotificationSettings
> => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (values: SpendNotificationSettings) => setSpendNotificationSettings(values),
    {
      onSuccess: () =>
        queryClient.refetchQueries([SPEND_NOTIFICATION_SETTINGS]),
      onError: (error) => toast.error(formatErrorMessage(error)),
    },
  );

  return mutation;
};
